// Arms the offline demo before anything can issue a request. Resolves to an
// empty module in every non-demo build (see demoRuntimePlugin in vite.config).
import 'virtual:march-demo-api'
import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { TrafficStreamProvider } from './contexts/TrafficStreamContext'
import { JobCenterProvider } from './contexts/JobCenterContext'
import { ActivePatientProvider } from './contexts/ActivePatientContext'
import { JobCenterLauncher } from './components/JobCenterLauncher'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ThemeProvider } from './context/ThemeContext'
import App from './App.tsx'
import { Toaster } from './components/ui/sonner'
import { SameOriginApiOriginBanner } from './components/SameOriginApiOriginBanner'
import { SkipToMainLink } from './components/SkipToMainLink'
import { SmartEntryGate } from './components/smart/SmartEntryGate'
import { AccessDeniedPage } from './components/AccessDeniedPage'
import { LaunchRequiredPage } from './auth/LaunchRequiredPage'
import { ClinicalWorkspaceGate, AdminRouteGuard } from './auth/RoleGate'
import { AuthProvider } from './auth/AuthProvider'
import { WorkspaceRedirect } from './auth/WorkspaceRedirect'
import { installUiEventTelemetry } from './services/uiEventTelemetry'
import './globals.css'

// #68 (USER ORDER 2026-08-09): capture the UI interaction stream so the
// combined-debug export carries click events alongside the request trace.
// Fire-and-forget; a failure here must never affect the app.
try {
  installUiEventTelemetry()
} catch {
  /* telemetry only */
}

// ── Admin console: lazy-loaded (UX-07) ──────────────────────────────────────
// The ops console pulls the heaviest dependencies in the app (mermaid,
// @xyflow/react, dagre) and none of it belongs in the clinician-facing entry
// chunk — clinicians land on /chat via SMART launch and may never open /admin.
// React.lazy needs default exports, so each line adapts the named export.
/* eslint-disable react-refresh/only-export-components */
const AdminJobs = React.lazy(async () => ({ default: (await import('./components/admin/AdminJobs')).AdminJobs }))
const AdminRules = React.lazy(async () => ({ default: (await import('./components/admin/AdminRules')).AdminRules }))
const AdminTools = React.lazy(async () => ({ default: (await import('./components/admin/AdminTools')).AdminTools }))
const AdminNotifications = React.lazy(async () => ({ default: (await import('./components/admin/AdminNotifications')).AdminNotifications }))
const AdminRunLookup = React.lazy(async () => ({ default: (await import('./components/admin/AdminRunLookup')).AdminRunLookup }))
const AdminStatus = React.lazy(async () => ({ default: (await import('./components/admin/AdminStatus')).AdminStatus }))
const AdminCapabilities = React.lazy(async () => ({ default: (await import('./components/admin/AdminCapabilities')).AdminCapabilities }))
const AdminSystemOverview = React.lazy(async () => ({ default: (await import('./components/admin/AdminSystemOverview')).AdminSystemOverview }))
const AdminSystemLog = React.lazy(async () => ({ default: (await import('./components/admin/AdminSystemLog')).AdminSystemLog }))
const TrafficInspectorPage = React.lazy(async () => ({ default: (await import('./components/admin/TrafficInspectorPage')).TrafficInspectorPage }))
const AdminRiskTwin = React.lazy(async () => ({ default: (await import('./components/admin/AdminRiskTwin')).AdminRiskTwin }))
const TwinAuditConsole = React.lazy(async () => ({ default: (await import('./components/admin/TwinAuditConsole')).TwinAuditConsole }))
const DigitalTwinPatientRoute = React.lazy(async () => ({ default: (await import('./components/digital-twin/DigitalTwinPatientRoute')).DigitalTwinPatientRoute }))
const ClinicianDigitalTwinRoute = React.lazy(async () => ({ default: (await import('./components/digital-twin/ClinicianDigitalTwinRoute')).ClinicianDigitalTwinRoute }))
const DigitalTwinV2Route = React.lazy(async () => ({ default: (await import('./components/digital-twin-v2/DigitalTwinV2Route')).DigitalTwinV2Route }))
const TrendVizRoute = React.lazy(async () => ({ default: (await import('./components/trend-viz/TrendVizRoute')).TrendVizRoute }))
const AdminPipeline = React.lazy(async () => ({ default: (await import('./components/admin/AdminPipeline')).AdminPipeline }))
const AuditPage = React.lazy(async () => ({ default: (await import('./components/admin/audit/AuditPage')).AuditPage }))
const IamAdminPage = React.lazy(async () => ({ default: (await import('./components/admin/iam/IamAdminPage')).IamAdminPage }))
const AdminShell = React.lazy(async () => ({ default: (await import('./components/admin/shell/AdminShell')).AdminShell }))
const AdminDashboard = React.lazy(async () => ({ default: (await import('./components/admin/v2/AdminDashboard')).AdminDashboard }))
const ComingSoonPage = React.lazy(async () => ({ default: (await import('./components/admin/v2/ComingSoonPage')).ComingSoonPage }))
/* eslint-enable react-refresh/only-export-components */

/** Shown while a lazy admin chunk loads — token-styled, no layout shift drama. */
function ConsoleLoading() {
  return (
    <div className="flex h-screen items-center justify-center bg-background" role="status" aria-live="polite">
      <span className="text-sm text-text-tertiary">Loading console…</span>
    </div>
  )
}

/**
 * UX-10: the legacy admin shell (/admin/legacy) was sunset on 2026-07-12 once
 * the Ops Console (/admin) reached feature parity. This keeps any lingering
 * bookmarks working by stripping the "/legacy" segment — /admin/legacy/rules ->
 * /admin/rules, /admin/legacy -> /admin — instead of a self-referential chain.
 */
function LegacyAdminRedirect() {
  const location = useLocation()
  const target = location.pathname.replace(/^\/admin\/legacy(?=\/|$)/, '/admin') + location.search
  return <Navigate to={target || '/admin'} replace />
}

const clinicalWorkspace = (
  <SmartEntryGate>
    <ClinicalWorkspaceGate>
      <App />
    </ClinicalWorkspaceGate>
  </SmartEntryGate>
)

// Same two clinical gates as /chat — a read-only patient page, not an admin one.
const clinicianDigitalTwin = (
  <SmartEntryGate>
    <ClinicalWorkspaceGate>
      <ClinicianDigitalTwinRoute />
    </ClinicalWorkspaceGate>
  </SmartEntryGate>
)

// The redesigned Digital Twin page (silhouette + 3-column layout). The v1
// route above stays intact as the rollback path — the Header button decides
// which one clinicians reach.
const clinicianDigitalTwinV2 = (
  <SmartEntryGate>
    <ClinicalWorkspaceGate>
      <DigitalTwinV2Route />
    </ClinicalWorkspaceGate>
  </SmartEntryGate>
)

// Trend/Record Visualization (register §10.2) — same clinical gates as /chat.
const clinicianTrendViz = (
  <SmartEntryGate>
    <ClinicalWorkspaceGate>
      <TrendVizRoute />
    </ClinicalWorkspaceGate>
  </SmartEntryGate>
)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <ErrorBoundary>
      <AuthProvider>
      {/* BASE_URL is '/' normally; on sub-path hosting (GitHub Pages serves
          the demo at /<repo>/) it is what keeps in-app links resolvable. */}
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <TrafficStreamProvider>
        <ActivePatientProvider>
        <JobCenterProvider>
        {/* WCAG 2.4.1: first tabbable on every page — jumps past the chrome to
            the active surface's <main id="main-content">. */}
        <SkipToMainLink />
        <SameOriginApiOriginBanner />
        <JobCenterLauncher />
        <Toaster position="top-center" toastOptions={{ classNames: { toast: 'mt-2' } }} />
        <Suspense fallback={<ConsoleLoading />}>
        <Routes>
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          {/* Clinical access, no SMART launch. Distinct from access-denied:
              nothing is denied, the patient context is simply absent. */}
          <Route path="/launch-required" element={<LaunchRequiredPage />} />
          {/* The whole-fleet console moved INTO the admin shell (/admin/jobs)
              so it gets the sidebar, breadcrumbs and command palette instead of
              floating outside the console it belongs to. /jobs keeps working. */}
          <Route path="/jobs" element={<Navigate to="/admin/jobs" replace />} />
          {/* March Ops Console — the live admin runs on this shell (sidebar +
              top bar + ⌘K palette + responsive drawer). The previous shell at
              /admin/legacy was sunset 2026-07-12 and now redirects here. */}
          <Route path="/admin" element={<AdminRouteGuard><AdminShell /></AdminRouteGuard>}>
            <Route index element={<AdminDashboard />} />
            <Route path="iam/*" element={<IamAdminPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="risk-twin" element={<AdminRiskTwin />} />
            <Route path="twin-audit" element={<TwinAuditConsole />} />
            {/* New Digital Twin view — clicking a SMART patient opens this. */}
            <Route path="digital-twin/:patientId" element={<DigitalTwinPatientRoute />} />
            {/* Back-compat: the four former risk pages live on one page now. */}
            <Route path="heartbeat" element={<Navigate to="/admin/risk-twin?tab=patients" replace />} />
            <Route path="digital-twin-core" element={<Navigate to="/admin/risk-twin?tab=twin" replace />} />
            <Route path="risk-engine-parameters" element={<Navigate to="/admin/twin-audit?tab=model" replace />} />
            <Route path="risk-engine-governance" element={<Navigate to="/admin/risk-twin?tab=governance" replace />} />
            <Route path="jobs" element={<AdminJobs />} />
            <Route path="pipeline" element={<AdminPipeline />} />
            <Route path="traffic-inspector" element={<TrafficInspectorPage />} />
            <Route path="system-log" element={<AdminSystemLog />} />
            <Route path="run-lookup" element={<AdminRunLookup />} />
            <Route path="status" element={<AdminStatus />} />
            <Route path="system-overview" element={<AdminSystemOverview />} />
            <Route path="capabilities" element={<AdminCapabilities />} />
            <Route path="rules" element={<AdminRules />} />
            <Route path="tools" element={<AdminTools />} />
            <Route path="notifications" element={<AdminNotifications />} />
            {/* Back-compat redirects for renamed audit URLs. */}
            <Route path="audit-log" element={<Navigate to="/admin/audit" replace />} />
            <Route path="iam/audit" element={<Navigate to="/admin/audit" replace />} />
            <Route path="*" element={<ComingSoonPage />} />
          </Route>
          {/* UX-10: the legacy admin shell was sunset 2026-07-12; /admin/legacy/*
              now redirects to the Ops Console, preserving deep links. */}
          <Route path="/admin/legacy/*" element={<LegacyAdminRedirect />} />
          {/* /admin/v2 is now the live /admin; keep old links working. */}
          <Route path="/admin/v2/*" element={<Navigate to="/admin" replace />} />
          <Route path="/chat" element={clinicalWorkspace} />
          {/* Clinician-facing digital twin — read-only, no build/reset. Opened
              from the patient drawer's "View full digital twin" link. */}
          <Route path="/patient/:patientId/digital-twin" element={clinicianDigitalTwin} />
          {/* The redesigned twin page — the Header's "Digital twin" button
              targets this; flipping the button back to /digital-twin is the
              one-line rollback. */}
          <Route path="/patient/:patientId/digital-twin-v2" element={clinicianDigitalTwinV2} />
          {/* Trend/Record Visualization — plot the windowed chart on a timeline. */}
          <Route path="/patient/:patientId/trend-viz" element={clinicianTrendViz} />
          {/* The standalone /workspace dashboard was removed — clinicians land
              on /chat (which runs behind the same SMART launch + clinical gates),
              admins on /admin. Old /workspace links redirect to the chat view. */}
          <Route path="/workspace" element={<Navigate to="/chat" replace />} />
          <Route path="/" element={<WorkspaceRedirect />} />
          <Route path="*" element={<WorkspaceRedirect />} />
        </Routes>
        </Suspense>
        </JobCenterProvider>
        </ActivePatientProvider>
        </TrafficStreamProvider>
      </BrowserRouter>
      </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </React.StrictMode>,
)
